import React, { FC, useEffect } from 'react';
import { useStorage } from '~Components/Storage';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Printer } from '~reducers';
import { TroubleShootButtons, TroubleShootingTextWrapper } from '../AddedPrinter';
import Button from '~ComponentsImproved/Button';
import { useDispatch, useI18n, useLogger } from '~Hooks';
import * as actions from '~actions';
import sidebarImage from '../../Images/sidebar-small.png';
import manualPrintImage from '../../Images/manual-print-guide.png';
import styled from 'styled-components';

const Images = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
  gap: 24px;

  img:first-child {
    max-height: max-content;
    width: 80px;
  }

  img:last-child {
    width: 500px;
    height: auto;
  }
`;

interface Props {
  printer: Printer;
  closeGuide: () => void;
  testPrint: () => void;
  printingWorks: (isPrinting: boolean) => void;
  sessionId: string;
}

const ReceiptsNotPrinting: FC<Props> = ({
  printer,
  closeGuide,
  testPrint,
  printingWorks,
  sessionId,
}) => {
  const logger = useLogger('printer-troubleshooting');
  const { i18n } = useI18n();
  const [storage, setStorage] = useStorage();
  const dispatch = useDispatch();

  useEffect(() => {
    logger.info('Troubleshooting step: RECEIPTS', {
      troubleShooting: {
        printer,
        storage,
        troubleShootingSessionId: sessionId,
      },
    });
  }, [storage, printer]);

  const makeThisPrimary = () => {
    window.postMessageBridge({
      setPrimary: {
        id: printer.id,
      },
    });

    closeGuide();
    logger.info('Printer set as primary via troubleshooting guide', {
      printer,
      troubleShootingSessionId: sessionId,
    });
    dispatch(actions.addToast(i18n('Printer.TS.Toast.PrimaryChange'), true));
  };

  const setAlwaysPrint = () => {
    setStorage({
      manualReceiptPrint: false,
      askToPrint: false,
    });
    closeGuide();
    logger.info('Manual print disabled via troubleshooting guide', {
      printer,
      troubleShootingSessionId: sessionId,
    });
    dispatch(actions.addToast(i18n('Printer.TS.Toast.PrintReceipts'), true));
  };

  if (!printer.primary) {
    return (
      <>
        <TroubleShootingTextWrapper>
          <Heading level={1} color="#fff" bottom={16}>
            {i18n('Printer.TS.NotPrimary')}
          </Heading>
          <Paragraph level={2} color="var(--colorBlueGray200)">
            {i18n('Printer.TS.NotPrimary.Desc')}
          </Paragraph>
        </TroubleShootingTextWrapper>
        <TroubleShootButtons>
          <Button
            text={i18n('Printer.Troubleshooting.No')}
            styleType="OK"
            onTouchStart={closeGuide}
          />
          <Button
            text={i18n('Printer.Troubleshooting.Yes')}
            styleType="OK"
            onTouchStart={makeThisPrimary}
          />
        </TroubleShootButtons>
      </>
    );
  }

  if (storage.manualReceiptPrint) {
    return (
      <>
        <TroubleShootingTextWrapper>
          <Heading level={1} color="#fff" bottom={16}>
            {i18n('Printer.TS.ManualPrintEnabled')}
          </Heading>
          <Paragraph level={2} color="var(--colorBlueGray200)">
            {i18n('Printer.TS.ManualPrintEnabled.Desc')}
          </Paragraph>
        </TroubleShootingTextWrapper>
        <Images>
          <img src={sidebarImage} />
          <img src={manualPrintImage} />
        </Images>
        <TroubleShootButtons>
          <Button
            text={i18n('Printer.TS.ManualPrintEnabled.Change')}
            styleType="OK"
            onTouchStart={setAlwaysPrint}
          />
          <Button
            text={i18n('Printer.Troubleshooting.OK')}
            styleType="OK"
            onTouchStart={closeGuide}
          />
        </TroubleShootButtons>
      </>
    );
  }

  return (
    <>
      <TroubleShootingTextWrapper>
        <Heading level={1} color="#fff" bottom={16}>
          {i18n('Printer.TS.DoesTestPrintWork')}
        </Heading>
        <Button
          text={i18n('Printer.Troubleshooting.TestPrint')}
          styleType="Confirm"
          onTouchStart={testPrint}
        />
      </TroubleShootingTextWrapper>
      <TroubleShootButtons>
        <Button
          text={i18n('Printer.Troubleshooting.No')}
          styleType="OK"
          onTouchStart={() => printingWorks(false)}
        />
        <Button
          text={i18n('Printer.Troubleshooting.Yes')}
          styleType="OK"
          onTouchStart={() => printingWorks(true)}
        />
      </TroubleShootButtons>
    </>
  );
};

export default ReceiptsNotPrinting;
